import { create } from "zustand";
import { getOpportunityStats } from "@/api/opportunity-api";
import { queryClient } from "@/lib/query-client";

interface DealStats {
  total: number;
  tracked: number;
  maxLimit: number;
  dealsRemaining: number;
}

interface DealStatsStore {
  dealStats: DealStats | null;
  loading: boolean;
  error: string | null;
  fetchDealStats: (opportunityId: string) => Promise<void>;
  getDealsRemaining: () => number;
  formatDealsRemaining: () => string;
}

export const useDealStatsStore = create<DealStatsStore>((set, get) => ({
  dealStats: null,
  loading: false,
  error: null,

  fetchDealStats: async (opportunityId: string) => {
    set({ loading: true, error: null });
    try {
      const response = await getOpportunityStats(opportunityId);
      if (response.status === "success") {
        const { total, tracked, maxLimit } = response.data;
        const dealsRemaining = Math.max(0, maxLimit - tracked);
        set({
          dealStats: { total, tracked, maxLimit, dealsRemaining },
          loading: false
        });
        // Tracked count just changed — drop the org-wide quota cache so the
        // sidebar tile and usage cards refetch instead of waiting on staleTime.
        queryClient.invalidateQueries({ queryKey: ["orgQuota"] });
      } else {
        set({ error: "Failed to fetch deal statistics", loading: false });
      }
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : "Failed to fetch deal statistics", 
        loading: false 
      });
    }
  },

  getDealsRemaining: () => {
    const { dealStats } = get();
    return dealStats?.dealsRemaining || 0;
  },

  formatDealsRemaining: () => {
    const dealsRemaining = get().getDealsRemaining();
    return new Intl.NumberFormat().format(dealsRemaining);
  },
}));